import { useLayoutEffect, useState } from 'react'

const isStudioReady = () => window.AccountHelper != null && window.WorkspaceHelper != null

/**
 * Polls until the design studio script (e.g. designer.min.js) has loaded and
 * window.AccountHelper and window.WorkspaceHelper are defined.
 * Use this when the main app may mount before the design bundle is loaded.
 *
 * @param timeoutMs - Max time to wait (default 100000). Rejects if studio not ready by then.
 * @returns Promise that resolves when both AccountHelper and WorkspaceHelper are on window.
 */
let sharedPromise: Promise<void> | null = null

export function whenStudioReady(timeoutMs: number = 100000): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('whenStudioReady: window is undefined')
  }
  if (isStudioReady()) {
    return Promise.resolve()
  }
  if (sharedPromise != null) {
    return sharedPromise
  }
  const pollIntervalMs = 100
  sharedPromise = new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs
    const id = window.setInterval(() => {
      if (isStudioReady()) {
        window.clearInterval(id)
        sharedPromise = null
        resolve()
        return
      }
      if (Date.now() >= deadline) {
        window.clearInterval(id)
        sharedPromise = null
        reject(new Error('whenStudioReady: design studio (AccountHelper/WorkspaceHelper) not available within timeout'))
      }
    }, pollIntervalMs)
  })
  return sharedPromise
}

export const useIsStudioReady = () => {
  const [ready, setReady] = useState(isStudioReady())
  useLayoutEffect(() => {
    whenStudioReady()
      .then(() => {
        setReady(true)
      })
      .catch((err) => {
        // Avoid an indefinite blank screen on `/myenergy` if the design bundle is slow or unavailable;
        // Proposal V2 / non-solar flows still mount and surface their own errors.
        console.warn('whenStudioReady: proceeding without full studio bootstrap', err)
        setReady(true)
      })
  }, [])

  return ready
}
